const pad = (value, length = 2) => String(value).padStart(length, "0");

export const formatSrtTimestamp = (milliseconds) => {
  const totalMs = Math.max(0, Math.floor(milliseconds));
  const hours = Math.floor(totalMs / 3600000);
  const minutes = Math.floor((totalMs % 3600000) / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)},${pad(ms, 3)}`;
};

export const formatVttTimestamp = (milliseconds) => {
  const totalMs = Math.max(0, Math.floor(milliseconds));
  const hours = Math.floor(totalMs / 3600000);
  const minutes = Math.floor((totalMs % 3600000) / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}.${pad(ms, 3)}`;
};

export const normalizeSegments = (segments = []) =>
  segments
    .map((segment, index) => {
      const startMs = Number(segment.startMs ?? segment.start ?? 0);
      const endMs = Number(segment.endMs ?? segment.end ?? startMs + 1000);
      const text = String(segment.text ?? segment.caption ?? "").trim();

      return {
        index: index + 1,
        startMs,
        endMs: endMs > startMs ? endMs : startMs + 1000,
        text,
      };
    })
    .filter((segment) => segment.text);

const DEVANAGARI_CHAR_RE = /[\u0900-\u097F]/;
const LATIN_SCRIPT_CHAR_RE = /[^A-Za-z0-9\s.,!?;:'"()\-]/g;

export const isPrimarilyDevanagari = (text = "") => {
  const value = String(text || "");
  const devanagariCount = (value.match(DEVANAGARI_CHAR_RE) || []).length;
  const latinCount = (value.match(/[A-Za-z]/g) || []).length;

  return devanagariCount > latinCount;
};

/** Keep English-alphabet transliteration only (Latin / Hinglish). */
export const toLatinScriptText = (text = "") =>
  String(text || "")
    .replace(DEVANAGARI_CHAR_RE, "")
    .replace(LATIN_SCRIPT_CHAR_RE, "")
    .replace(/\s+/g, " ")
    .trim();

export const normalizeLatinSegments = (segments = []) =>
  normalizeSegments(segments)
    .map((segment) => ({
      ...segment,
      text: toLatinScriptText(segment.text),
    }))
    .filter((segment) => segment.text);

export const buildLatinTranscriptText = (segments = []) =>
  normalizeLatinSegments(segments)
    .map((segment) => segment.text)
    .join("\n");

export const normalizePhoneticSegments = (segments = []) =>
  segments
    .map((segment, index) => {
      const startMs = Number(segment.startMs ?? segment.start ?? 0);
      const endMs = Number(segment.endMs ?? segment.end ?? startMs + 1000);
      const text = String(
        segment.phoneticText ??
          segment.phonetic ??
          segment.textDevanagari ??
          segment.devanagariText ??
          segment.text ??
          "",
      ).trim();

      return {
        index: index + 1,
        startMs,
        endMs: endMs > startMs ? endMs : startMs + 1000,
        text,
      };
    })
    .filter((segment) => segment.text);

export const buildTranscriptText = (segments = []) =>
  normalizeSegments(segments)
    .map((segment) => segment.text)
    .join("\n");

export const buildPhoneticTranscriptText = (segments = []) =>
  normalizePhoneticSegments(segments)
    .map((segment) => segment.text)
    .join("\n");

export const buildSrtContent = (segments = []) =>
  normalizeSegments(segments)
    .map(
      (segment) =>
        `${segment.index}\n${formatSrtTimestamp(segment.startMs)} --> ${formatSrtTimestamp(segment.endMs)}\n${segment.text}`,
    )
    .join("\n\n");

export const buildVttContent = (segments = []) => {
  const cues = normalizeSegments(segments)
    .map(
      (segment) =>
        `${formatVttTimestamp(segment.startMs)} --> ${formatVttTimestamp(segment.endMs)}\n${segment.text}`,
    )
    .join("\n\n");

  return `WEBVTT\n\n${cues}\n`;
};

export const downloadTextFile = (filename, content, mimeType = "text/plain") => {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const getSubtitleBaseName = (fileName = "video") =>
  fileName.replace(/\.[^/.]+$/, "") || "video";

export const getPhoneticDownloadBaseName = (fileName = "video") =>
  `${getSubtitleBaseName(fileName)}-phonetic`;

export const ACCEPTED_VIDEO_TYPES = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-matroska",
];

export const ACCEPTED_VIDEO_EXTENSIONS = [".mp4", ".webm", ".mov", ".avi", ".mkv"];

export const MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024;
export const FREE_PLAN_MAX_DURATION_MINUTES = 20;
export const FREE_PLAN_MAX_TOTAL_DURATION_HOURS = 3;
export const FREE_PLAN_MAX_TOTAL_DURATION_SECONDS =
  FREE_PLAN_MAX_TOTAL_DURATION_HOURS * 60 * 60;
/** Warn before upload — API Gateway returns 413 above ~10 MB */
export const API_GATEWAY_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const buildVideoSizeLimitMessage = () =>
  `Video is too large. Maximum supported size is ${MAX_VIDEO_SIZE_BYTES / (1024 * 1024)} MB on the free plan. Please upgrade to a paid subscription for higher limits and priority processing.`;

export const validateVideoFile = (file) => {
  if (!file) {
    return "Please select a video file.";
  }

  const extension = `.${file.name.split(".").pop()?.toLowerCase() || ""}`;
  const hasValidType =
    ACCEPTED_VIDEO_TYPES.includes(file.type) ||
    ACCEPTED_VIDEO_EXTENSIONS.includes(extension);

  if (!hasValidType) {
    return "Unsupported video format. Upload MP4, WebM, MOV, AVI, or MKV.";
  }

  if (file.size > MAX_VIDEO_SIZE_BYTES) {
    return buildVideoSizeLimitMessage();
  }

  return "";
};

export const getVideoDurationSeconds = (file) =>
  new Promise((resolve, reject) => {
    if (typeof document === "undefined" || !file) {
      reject(new Error("Video duration check is only available in the browser."));
      return;
    }

    const video = document.createElement("video");
    video.preload = "metadata";
    const objectUrl = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(Number.isFinite(video.duration) ? video.duration : 0);
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to read video duration."));
    };

    video.src = objectUrl;
  });

export const formatVideoDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return "Duration unavailable";
  }

  const totalSeconds = Math.round(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  return `${minutes}:${String(secs).padStart(2, "0")}`;
};

export const validateVideoDuration = async (file) => {
  try {
    const durationSeconds = await getVideoDurationSeconds(file);

    if (durationSeconds > FREE_PLAN_MAX_DURATION_MINUTES * 60) {
      const durationMinutes = Math.ceil(durationSeconds / 60);
      return {
        durationSeconds,
        error: `Video is too long (${durationMinutes} minutes). Free plan allows up to ${FREE_PLAN_MAX_DURATION_MINUTES} minutes per video. Please upgrade to a paid subscription for longer videos.`,
      };
    }

    return { durationSeconds, error: "" };
  } catch {
    return { durationSeconds: null, error: "" };
  }
};

export const NO_AUDIO_DETECTED_MESSAGE =
  "No audio detected. Please upload a valid video with clear audio and try again.";

const WHISPER_PROMPT_ECHO_MARKERS = [
  "hinglish mein likho",
  "devanagari mat likho",
  "ye roman script",
  "namaste doston, aaj hum is video mein baat karenge",
];

const normalizeDetectionText = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const isNoAudioTranscript = (transcript = "", segments = []) => {
  const normalized = normalizeDetectionText(transcript);
  const segmentText = segments
    .map((segment) => normalizeDetectionText(segment.text || segment.phoneticText))
    .filter(Boolean)
    .join(" ");
  const combined = normalized || segmentText;

  if (!combined) {
    return true;
  }

  let remainder = combined;

  WHISPER_PROMPT_ECHO_MARKERS.forEach((marker) => {
    remainder = remainder.replace(new RegExp(marker.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), " ");
  });

  remainder = remainder
    .replace(/[.,!?;:\-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const hasPromptEcho = WHISPER_PROMPT_ECHO_MARKERS.some((marker) => combined.includes(marker));

  if (hasPromptEcho && remainder.length < 15) {
    return true;
  }

  const hallucinationPhrase = "hinglish mein likho, devanagari mat likho";

  if (combined.split(hallucinationPhrase).length - 1 >= 2) {
    return true;
  }

  if (
    combined.includes("devanagari mat likho") &&
    combined.includes("hinglish mein likho") &&
    remainder.length < 30
  ) {
    return true;
  }

  return false;
};
